import type * as React from "react";
import { type ChangeEvent, type FC, type ReactNode, useMemo } from "react";

import Spacer from "../../common/components/Spacer";
import { IconType } from "../../common/components/icon/IconType";
import { SimpleSelect } from "../../common/components/Select/SimpleSelect";
import { type WishlistModel } from "../model";
import dayjs from "dayjs";
import { DateFormatType, formatDate } from "../../utils/DateFormatter";
import { type DropdownEntry } from "~/common/types";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { useBooleanState } from "~/common/hooks/useBooleanState";

interface WishlistDropdownProps {
  className?: string;
  wishlists: WishlistModel[];
  selectedWishlist?: string | null;
  onWishlistSelect: (wishlistId?: string) => void;
  onNewWishlist?: () => void;
  disabled?: boolean;

  label?: ReactNode;
  error?: boolean;
}

const WishlistDropdown: FC<WishlistDropdownProps> = (props) => {
  const { className, wishlists, selectedWishlist, onWishlistSelect, disabled } = props;
  const { val: dropdownStatus, setTrue: open, setFalse: close } = useBooleanState(false);
  const { t } = useTranslation();
  const onSelect = (e: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    if (!e.isAdd) {
      onWishlistSelect(e.target.value as string);
    }
  };

  const onAdd = (e: React.MouseEvent) => {
    // @ts-ignore
    e.isAdd = true;
    props.onNewWishlist?.();
    close();
  };

  const wishlistEntries: DropdownEntry<string>[] = useMemo(() => {
    return wishlists.map((wishlist) => {
      const name = wishlist.name;
      const date = dayjs.utc(wishlist.lastModification).toDate();
      const lastModified = formatDate(date, DateFormatType.DATE);

      return {
        value: wishlist.id,
        label: (
          <div className="d-flex flex-justify-between w-100">
            <span className={"scha__wishlist__dropdown__option__text"}>
              {t("wishlistDropdown.entry", { name, lastModified })}
            </span>
            <span className={"scha__wishlist__dropdown__option__count"}>({wishlist.entryIds.length})</span>
          </div>
        ),
      };
    });
  }, [wishlists, t]);

  const label = props.label ?? t("wishlistPage.dropdownLabel");

  return (
    <SimpleSelect
      fullWidth={true}
      values={wishlistEntries}
      value={selectedWishlist}
      label={label}
      className={className}
      data-test-id={"dropdown-select-wishlist"}
      onChange={onSelect}
      disabled={disabled}
      error={props.error}
      onClose={close}
      onOpen={open}
      open={dropdownStatus}
    >
      {wishlistEntries.length < 1 && (
        <div className="p-2 f-label-color">{t("wishlist.dropdown.noWishlistMessage")}</div>
      )}
      {props.onNewWishlist && (
        <>
          <Spacer className={"mt-2 mb-0"} />
          <Button
            startIcon={<DefaultButtonIcon type={IconType.Plus} />}
            data-test-id={"dropdown-select-wishlist-btn-new-wishlist"}
            fullWidth={true}
            onClick={onAdd}
            variant="text"
          >
            {t("wishlistDropdown.add")}
          </Button>
        </>
      )}
    </SimpleSelect>
  );
};

export default WishlistDropdown;
